/**
 * Custom Norse SVG icon family — Midgard navigation identity.
 *
 * All icons: 24×24 viewBox, stroke-based (stroke="currentColor"), fill="none" on root svg.
 * HearthIcon door uses --mg-gold for a constant amber hearth glow.
 * Each icon accepts a `className` prop for sizing (e.g. "w-5 h-5").
 */

/* ─── The Hearth (Home) ───────────────────────────────────────────────────── */
export function HearthIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      {/* Chimney — sits on left roof slope, visually pierces through it */}
      <rect x="7.5" y="2" width="2.5" height="6.5" rx="0.3"
        stroke="currentColor" strokeWidth="1.5" />
      {/* Steep A-frame roof */}
      <polyline points="2,12 12,3 22,12"
        stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      {/* Log wall */}
      <rect x="5" y="12" width="14" height="9"
        stroke="currentColor" strokeWidth="1.5" />
      {/* Log seam lines extend 1.5 px past wall edges — log-end protrusion effect */}
      <line x1="3.5" y1="14.5" x2="20.5" y2="14.5"
        stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      <line x1="3.5" y1="17.5" x2="20.5" y2="17.5"
        stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      {/* Door — amber hearth glow, always warm regardless of active state */}
      <rect x="9.5" y="17" width="5" height="4" rx="0.5"
        fill="hsl(var(--mg-gold))" fillOpacity="0.65"
        stroke="currentColor" strokeWidth="1.25" />
    </svg>
  );
}

/* ─── Wayfinder (Explore) ─────────────────────────────────────────────────── */
export function WayfinderIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      {/* N point — filled to mark cardinal direction (navigator's north) */}
      <path d="M12 3 L10.5 11 L12 9.5 L13.5 11 Z"
        fill="currentColor" />
      {/* S point */}
      <path d="M12 21 L10.5 13 L12 14.5 L13.5 13 Z"
        stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
      {/* E point */}
      <path d="M21 12 L13 10.5 L14.5 12 L13 13.5 Z"
        stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
      {/* W point */}
      <path d="M3 12 L11 10.5 L9.5 12 L11 13.5 Z"
        stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

/* ─── Sagas (Reels) ───────────────────────────────────────────────────────── */
export function SagasIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      {/* Scroll side edges */}
      <line x1="8" y1="5" x2="8" y2="19" stroke="currentColor" strokeWidth="1.5" />
      <line x1="16" y1="5" x2="16" y2="19" stroke="currentColor" strokeWidth="1.5" />
      {/* Top roll — back arc (top of cylinder) */}
      <path d="M8 5 Q 12 3 16 5"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      {/* Top roll — front arc (bottom of cylinder) */}
      <path d="M8 5 Q 12 7 16 5"
        stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      {/* Bottom roll — back arc */}
      <path d="M8 19 Q 12 21 16 19"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      {/* Bottom roll — front arc */}
      <path d="M8 19 Q 12 17 16 19"
        stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      {/* Runic content lines */}
      <line x1="10.5" y1="9" x2="13.5" y2="9"
        stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      <line x1="10.5" y1="12" x2="13.5" y2="12"
        stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      <line x1="10.5" y1="15" x2="13.5" y2="15"
        stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
    </svg>
  );
}

/* ─── The Realms (World) — Yggdrasil world tree ───────────────────────────── */
export function TheRealmsIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      {/* Canopy sphere — the nine realms */}
      <circle cx="12" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.5" />
      {/* Inner branch lines within canopy */}
      <path d="M12 8 L9.5 5.5"
        stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      <path d="M12 8 L14.5 5.5"
        stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      <path d="M12 8 L12 3"
        stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      {/* Trunk */}
      <line x1="12" y1="13.5" x2="12" y2="20"
        stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      {/* Three roots fanning out */}
      <path d="M12 20 L8 23"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12 20 L16 23"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12 20 L12 23"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/* ─── Ravenpost (Messages) — Odin's ravens carry messages ────────────────── */
export function RavenpostIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      {/* Wings — M-silhouette raven from the front, wings spread in flight */}
      <path d="M2 14 C5 9 9 7.5 11 10 L12 12 L13 10 C15 7.5 19 9 22 14"
        stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      {/* Body / tail — narrow diamond drop */}
      <path d="M12 12 L10.5 19 L12 21 L13.5 19 Z"
        stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      {/* Head */}
      <circle cx="12" cy="7.5" r="2" stroke="currentColor" strokeWidth="1.5" />
      {/* Beak — downward V connecting head to body */}
      <path d="M10.5 9 L12 11 L13.5 9"
        stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─── The Herald (Notifications) — Gjallarhorn, Heimdall's warning horn ──── */
export function TheHeraldIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      {/* Horn body — top edge, narrows toward mouthpiece (lower-left) */}
      <path d="M3 18 C6 16 11 12 17 10 C19 9.5 20.5 9.5 21 9.5"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      {/* Horn body — bottom edge */}
      <path d="M4.5 20 C7 18.5 11 15.5 17 13.5 C19 13 20.5 13 21 13"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      {/* Bell flare (right end arc) */}
      <path d="M21 9.5 C23 10 23 12.5 21 13"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      {/* Mouthpiece (left end — narrow closing stroke) */}
      <line x1="3" y1="18" x2="4.5" y2="20"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Sound waves emanating from bell */}
      <path d="M14 7.5 C16 8 17 9 16.5 10.5"
        stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      <path d="M13 5 C17 5.5 19.5 8 19.5 11"
        stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

/* ─── Keepsake (Bookmarks) — carved Norse token/pendant ──────────────────── */
export function KeepsakeIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      {/* Bookmark ribbon — pointed V at bottom */}
      <path d="M7 3 H17 V21 L12 17 L7 21 Z"
        stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      {/* Hanging hole — like a Norse runestone pendant */}
      <circle cx="12" cy="6" r="1.5"
        stroke="currentColor" strokeWidth="1.25" />
      {/* Carved horizontal marks */}
      <line x1="9.5" y1="9.5" x2="14.5" y2="9.5"
        stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      <line x1="9.5" y1="12.5" x2="14.5" y2="12.5"
        stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
    </svg>
  );
}
